import React from 'react';
import { Input, DatePicker, Modal } from 'antd';
import { SearchOutlined } from 'antd/icons';
import moment from 'moment';
import format from 'vl-common/src/lib/formatDate';
import { FilterTrigger } from './FilterTrigger';
import { filterPopupClassName, focusFilterPopup, restoreFilterTriggerFocus } from './filterPopupFocus';
import i18n from 'i18next';

const t = i18n.getFixedT(null, 'common');

const queryChanged = (setSelectedKeys, value, dataIndex, setQuery, confirm, dateStart) => {
  if (value === 'Invalid date') {
    value = '';
  }
  setSelectedKeys(value ? [value] : []);
  setQuery((query) => ({ ...query, [dataIndex]: value === '' ? undefined : value }));
  if (dataIndex !== dateStart) {
    confirm();
  }
};

const showVLIdSearchError = (searchPreffix) => {
  return Modal.error({
    title: 'Invalid search term',
    content: (
      <>
        <p>
          {t('Please ensure your search term includes at least 4 numeric characters and does not begin with any zeros')}
        </p>
        <p>
          {t('Please note:')} {searchPreffix || 'NDL00'} {t('will not be included in the search results.')}
        </p>
      </>
    )
  });
};

const showNumericSearchError = () => {
  return Modal.error({
    title: 'Invalid search term',
    content: <p>{t('Please enter a numeric value. Letters and special characters are not allowed.')}</p>
  });
};

const validateVLIdSearch = (search) => {
  if (!search) {
    return true;
  }
  if (search.length < 4) {
    showVLIdSearchError();
    return false;
  }
  const match = search.match(/^(\D+0*)(\d.*)?$/); //NOSONAR client side DOS doesn't apply!
  const nonNumericPrefix = match?.[1] || '';
  const searchPart = match?.[2] || '';
  if (searchPart.length < 4 && nonNumericPrefix.length) {
    showVLIdSearchError(nonNumericPrefix);
    return false;
  }
  return true;
};

const validateNumericSearch = (search) => {
  if (!search) {
    return true;
  }

  if (!/^\d*$/.test(search)) {
    showNumericSearchError();
    return false;
  }
  return true;
};

export const filterColumn = ({
  placeholder,
  dataIndex,
  setQuery,
  query,
  dateFilter,
  dob,
  filterable,
  columnKey,
  active
}) => {
  const filterKey = `${columnKey ?? dataIndex ?? ''}`;
  const rootClassName = filterPopupClassName(filterKey);

  // @ts-ignore
  return {
    filterDropdown: ({ setSelectedKeys, confirm }) => {
      if (!dateFilter) {
        return (
          <Input
            defaultValue={query[dataIndex]}
            placeholder={t('Search {{placeholder}}', { placeholder })}
            onPressEnter={(e) => {
              const { value } = e.currentTarget;
              if (filterable.vlId) {
                const isValidVLIdSearch = validateVLIdSearch(value);
                if (!isValidVLIdSearch) return;
              }
              if (filterable.numeric) {
                const isValidNumericSearch = validateNumericSearch(value);
                if (!isValidNumericSearch) return;
              }
              queryChanged(setSelectedKeys, value, dataIndex, setQuery, confirm, filterable.dateStartFilterKey);
            }}
            suffix={
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#888'
                }}
              >
                {t('Press ↵ to search')}
              </span>
            }
          />
        );
      }

      if (!dob) {
        return (
          <div style={{ display: 'grid' }}>
            <DatePicker
              defaultValue={
                query[filterable.dateStartFilterKey] ? moment(query[filterable.dateStartFilterKey], 'YYYY-MM-DD') : null
              }
              placeholder={t('From:')}
              format="YYYY-MM-DD"
              onChange={(date, ds) =>
                queryChanged(
                  setSelectedKeys,
                  ds ? format(new Date(ds), 'yyyy-MM-dd') : null,
                  filterable.dateStartFilterKey,
                  setQuery,
                  confirm,
                  ''
                )
              }
            />
            <DatePicker
              defaultValue={
                query[filterable.dateEndFilterKey] ? moment(query[filterable.dateEndFilterKey], 'YYYY-MM-DD') : null
              }
              placeholder={t('To:')}
              format="YYYY-MM-DD"
              onChange={(date, ds) =>
                queryChanged(
                  setSelectedKeys,
                  ds ? format(new Date(ds), 'yyyy-MM-dd') : null,
                  filterable.dateEndFilterKey,
                  setQuery,
                  confirm,
                  filterable.dateStartFilterKey
                )
              }
            />
          </div>
        );
      }

      return (
        <DatePicker
          defaultValue={query[dataIndex] ? moment(query[dataIndex], 'YYYY-MM-DD') : moment()}
          placeholder={t('DD/MM/YYYY')}
          format="DD/MM/YYYY"
          onChange={(e) =>
            queryChanged(
              setSelectedKeys,
              moment(e).format('YYYY-MM-DD'),
              'dob',
              setQuery,
              confirm,
              filterable.dateStartFilterKey
            )
          }
        />
      );
    },
    filterDropdownProps: {
      rootClassName,
      children: (
        <FilterTrigger
          columnTitle={placeholder}
          columnKey={filterKey}
          active={!!active}
          icon={<SearchOutlined style={{ color: active ? 'var(--productPrimaryDark)' : undefined }} />}
        />
      ),
      onOpenChange: (open) => {
        if (open) focusFilterPopup({ rootClassName, focusSelector: 'input' });
        else restoreFilterTriggerFocus({ rootClassName });
      }
    }
  };
};
